import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ContextMenuItemType,
  OutlinedIcons,
  getFormattedDateShortMonthDDYYYY,
} from '@marageti/z4-lib';
import {
  Ticket,
  TicketStatus,
  TicketSubType,
} from '@marageti/z4-sdk/lib/tickets';
import SeverityIcon from '../../ticket-severity-icon/ticket-severity-icon';
import ContextMenuComplete from '../context-menu';
import {
  STATUS_DISPLAY_LABELS,
  STATUS_MENU_OPTIONS,
  SUBTYPE_DISPLAY_LABELS,
  SEVERITY_CHIPS,
} from '../../../pages/tickets/options';

const {
  OutlinedSelectorIcon,
  OutlinedExclamationIcon,
  OutlinedEmojiSadIcon,
  OutlinedStarIcon,
  OutlinedStatusOfflineIcon,
  OutlinedOfficeBuildingIcon,
  OutlinedCashIcon,
  OutlinedBanIcon,
  OutlinedInformationCircleIcon,
  OutlinedBellIcon,
} = OutlinedIcons;

type TicketCardViewProps = {
  ticket: Ticket;
  onEdit: () => void;
  onReassign: () => void;
  onDelete: () => void;
  onStatusChange: (ticketId: string, status: TicketStatus) => void;
};

const STATUS_CHIP_COLOR: Record<
string,
'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'
> = {
  PENDING: 'default',
  WAITING_ON_TRAVELER_CARE: 'info',
  WAITING_ON_PARTNERSHIP: 'warning',
  COMPLETED: 'primary',
  CANCELED: 'error',
};

const getSubtypeIcon = (
  subType: TicketSubType,
  isMobile: boolean = false,
  subtypeTooltip?: string,
  isLoading: boolean = false,
): React.ReactElement => {
  const size = isMobile ? '16px' : '24px';
  const iconColor = subType === 'TRAVELER_ISSUE' ? 'error.main' : 'text.primary';
  const backgroundColor = subType === 'TRAVELER_ISSUE' ? 'error.light' : 'common.grey';
  const containerSize = isMobile ? '25px' : '40px';
  const boxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor,
    borderRadius: '50%',
    minWidth: containerSize,
    width: containerSize,
    height: containerSize,
  };

  if (isLoading) {
    return (
      <Box sx={{ ...boxSx }}>
        <CircularProgress size={isMobile ? 14 : 20} color="inherit" />
      </Box>
    );
  }

  let icon = null;
  switch (subType) {
    case 'TRAVELER_ISSUE':
      icon = (
        <OutlinedExclamationIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'TRAVELER_BEHAVIOR':
      icon = (
        <OutlinedEmojiSadIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'TRAVELER_REQUESTED_OFFLINE':
      icon = (
        <OutlinedStatusOfflineIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'TRAVELER_REVIEW':
      icon = (
        <OutlinedStarIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'PARTNER_OPERATIONS':
      icon = (
        <OutlinedOfficeBuildingIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'PARTNER_BEHAVIOR':
      icon = (
        <OutlinedEmojiSadIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'PARTNER_REQUESTED_OFFLINE':
      icon = (
        <OutlinedStatusOfflineIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'UNREPORTED_SALE':
      icon = (
        <OutlinedCashIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    case 'TRIP_CANCELLATION':
      icon = (
        <OutlinedBanIcon sx={{ color: iconColor, width: size, height: size }} />
      );
      break;
    case 'OTHER':
      icon = (
        <OutlinedInformationCircleIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
      break;
    default:
      icon = (
        <OutlinedBellIcon
          sx={{ color: iconColor, width: size, height: size }}
        />
      );
  }
  return (
    <Tooltip title={subtypeTooltip}>
      <Box sx={{ ...boxSx, padding: 1 }}>
        {icon}
      </Box>
    </Tooltip>
  );
};

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

type TicketCardContextMenuProps = {
  ticket: Ticket;
  onEdit: () => void;
  onReassign: () => void;
  onDelete: () => void;
};

const TicketCardContextMenu = ({
  ticket,
  onEdit,
  onReassign,
  onDelete,
}: TicketCardContextMenuProps) => {
  const items: ContextMenuItemType[] = [
    { label: 'Edit', action: onEdit, disabled: ticket.status === 'COMPLETED' },
    { label: 'Reassign', action: onReassign },
    { label: 'Delete', action: onDelete },
  ];
  return <ContextMenuComplete id={`ticket-${ticket.id}`} items={items} />;
};

const TicketCardView = ({
  ticket,
  onEdit,
  onReassign,
  onDelete,
  onStatusChange,
}: TicketCardViewProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));

  const handleChipClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = async (newStatus: TicketStatus) => {
    handleMenuClose();
    setIsUpdating(true);
    try {
      await onStatusChange(ticket.id, newStatus);
    } finally {
      setIsUpdating(false);
    }
  };

  const isClosed = ticket.status === 'COMPLETED';
  const assigneeFirstNames = ticket.assignees?.map((a) => a.name.split(' ')[0]).join(', ')
    || 'Unassigned';
  const assigneeFullNames = ticket.assignees?.map((a) => a.name).join(', ') || 'Unassigned';
  const subtypeLabel = ticket.ticketSubType
    ? SUBTYPE_DISPLAY_LABELS[ticket.ticketSubType] || ticket.ticketSubType
    : '';
  const statusLabel = STATUS_DISPLAY_LABELS[ticket.status] || ticket.status;
  const descriptionPreview = ticket.description
    ? stripHtml(ticket.description)
    : '';

  const severityLabel = SEVERITY_CHIPS.find((c) => c.value === ticket.outcomeSeverity)?.label || '';
  const severityTooltip = severityLabel
    ? `${severityLabel}${
      ticket.outcomeComments ? `: ${ticket.outcomeComments}` : ''
    }`
    : '';

  // #region layout elements *********************************************************************
  const titleSx = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
  };

  const descriptionSx = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const titleElement = (
    <Typography variant="h4" sx={titleSx}>
      {ticket.title}
    </Typography>
  );
  const descriptionElement = descriptionPreview && (
    <Typography variant="caption" color="text.secondary" sx={descriptionSx}>
      {descriptionPreview}
    </Typography>
  );

  const renderTypeTravelerCreated = () => (
    <Stack spacing={0.5}>
      {subtypeLabel && (
        <Typography variant="caption" color="text.primary" noWrap>
          <Typography variant="caption" color="text.secondary">Type: </Typography>
          {subtypeLabel}
        </Typography>
      )}
      <Typography variant="caption" color="text.primary" noWrap>
        <Typography variant="caption" color="text.secondary">Traveler: </Typography>
        {ticket.travelerId ? (
          <Box
            component={Link}
            to={`/travelers/${ticket.travelerId}`}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            target="_blank"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {ticket.travelerName || '-'}
          </Box>
        ) : (
          ticket.travelerName || '-'
        )}
      </Typography>
      <Typography variant="caption" color="text.primary" noWrap>
        <Typography variant="caption" color="text.secondary">Created: </Typography>
        {getFormattedDateShortMonthDDYYYY(ticket.createdAt) || ''}
      </Typography>
    </Stack>
  );

  const renderAssigneePartnerUpdated = () => (
    <Stack spacing={0.25}>
      <Tooltip title={assigneeFullNames}>
        <Typography variant="caption" color="text.primary" noWrap>
          <Typography variant="caption" color="text.secondary">Assignee: </Typography>
          {assigneeFirstNames}
        </Typography>
      </Tooltip>
      <Tooltip title={ticket.partnerName}>
        <Typography variant="caption" color="text.primary" noWrap>
          <Typography variant="caption" color="text.secondary">Partner: </Typography>
          {ticket.partnerId ? (
            <Box
              component={Link}
              to={`/partners/${ticket.partnerId}`}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              target="_blank"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {ticket.partnerName || '-'}
            </Box>
          ) : (
            ticket.partnerName || '-'
          )}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.primary" noWrap>
        <Typography variant="caption" color="text.secondary">Updated: </Typography>
        {getFormattedDateShortMonthDDYYYY(ticket.updatedAt) || ''}
      </Typography>
    </Stack>
  );

  const statusChipElement = (
    <Chip
      label={statusLabel}
      color={STATUS_CHIP_COLOR[ticket.status] || 'default'}
      onClick={isUpdating ? undefined : handleChipClick}
      deleteIcon={
        isUpdating
          ? <CircularProgress size={14} color="inherit" />
          : <OutlinedSelectorIcon sx={{ fontSize: 18 }} />
      }
      onDelete={isUpdating ? undefined : handleChipClick}
      sx={{ pr: 0.5, cursor: isUpdating ? 'default' : 'pointer' }}
    />
  );

  const severityIconElement = isClosed && ticket.outcomeSeverity && (
    <Tooltip title={severityTooltip}>
      <span style={{ display: 'flex' }}>
        <SeverityIcon severity={ticket.outcomeSeverity} />
      </span>
    </Tooltip>
  );

  const statusMenuElement = (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      onClick={(e) => e.stopPropagation()}
    >
      {STATUS_MENU_OPTIONS.map((option) => (
        <MenuItem
          key={option.value}
          disabled={option.value === ticket.status}
          onClick={() => handleStatusSelect(option.value)}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  );

  const contextMenuElement = (
    <Box sx={{ ...(isUpdating && { pointerEvents: 'none', opacity: 0.5 }) }}>
      <TicketCardContextMenu
        ticket={ticket}
        onEdit={onEdit}
        onReassign={onReassign}
        onDelete={onDelete}
      />
    </Box>
  );
  // #endregion

  return (
    <Card
      component={Link}
      to={`/tickets/${ticket.id}`}
      sx={{ textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: 'grey.A100' } }}
    >
      <CardContent>
        {isMobile ? (
          // #region Mobile  *********************************************************************
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {getSubtypeIcon(ticket.ticketSubType, isMobile, undefined, isUpdating)}
              {titleElement}
            </Stack>
            {descriptionElement}
            <Grid container alignItems="center" spacing={0}>
              <Grid item xs={12} sm={6}>
                {renderTypeTravelerCreated()}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderAssigneePartnerUpdated()}
              </Grid>
            </Grid>

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {statusChipElement}
                {severityIconElement}
                {statusMenuElement}
              </Box>
              {contextMenuElement}
            </Stack>
          </Stack>
        ) : (
          // #endregion
          // #region Desktop *********************************************************************
          <Grid container alignItems="center" spacing={3} wrap="nowrap">
            {/* icon + Title + description */}
            <Grid item xs overflow="hidden">
              <Stack direction="row" spacing={3} alignItems="center">
                {getSubtypeIcon(ticket.ticketSubType, isMobile, subtypeLabel, isUpdating)}
                <Stack spacing={0.5}>
                  {titleElement}
                  {descriptionElement}
                </Stack>
              </Stack>
            </Grid>

            <Grid item md={2.1}>
              {renderTypeTravelerCreated()}
            </Grid>

            <Grid item md={2.1}>
              {renderAssigneePartnerUpdated()}
            </Grid>

            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                width: '250px',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {severityIconElement}
                {statusChipElement}
                {statusMenuElement}
                {contextMenuElement}
              </Stack>
            </Grid>
          </Grid>
          // #endregion
        )}
      </CardContent>
    </Card>
  );
};

export default TicketCardView;
